import React, { useState, useEffect } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Button,
  Grid,
  Paper,
  Switch,
  FormControlLabel,
} from "@mui/material";

/**
 * EditQuestion
 * Tutor-facing question editor (MC + FITB aware)
 */
const EditQuestion = ({ open, onClose, question, onSave }) => {
  const [localQ, setLocalQ] = useState(null);

  useEffect(() => {
    if (question) {
      setLocalQ(question);
    }
  }, [question]);

  if (!localQ) return null;

  const answerType = localQ.answer?.type ?? "mc";
  const isMC = answerType === "mc";

  const updateField = (field, value) => {
    setLocalQ((prev) => ({ ...prev, [field]: value }));
  };

  const updateAnswer = (field, value) => {
    setLocalQ((prev) => ({
      ...prev,
      answer: {
        ...(prev.answer || {}),
        [field]: value,
      },
    }));
  };

  const updateChoice = (letter, value) => {
    setLocalQ((prev) => ({
      ...prev,
      answerChoices: {
        ...(prev.answerChoices || {}),
        [letter]: value,
      },
    }));
  };

  const toggleAnswerType = () => {
    setLocalQ((prev) => ({
      ...prev,
      answer: {
        ...(prev.answer || {}),
        type: isMC ? "fitb" : "mc",
      },
    }));
  };

  const handleSave = () => {
    onSave?.(localQ);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Question</DialogTitle>

      <DialogContent dividers>
        {/* ===== METADATA ===== */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography fontWeight={600} gutterBottom>
            Metadata
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="Question ID"
                value={localQ.id}
                fullWidth
                disabled
              />
            </Grid>

            <Grid item xs={3}>
              <TextField
                label="Difficulty"
                type="number"
                value={localQ.difficulty ?? ""}
                onChange={(e) =>
                  updateField("difficulty", Number(e.target.value))
                }
                fullWidth
              />
            </Grid>

            <Grid item xs={3}>
              <TextField
                label="Estimated Time (sec)"
                type="number"
                value={localQ.estimatedTimeSeconds ?? ""}
                onChange={(e) =>
                  updateField(
                    "estimatedTimeSeconds",
                    Number(e.target.value)
                  )
                }
                fullWidth
              />
            </Grid>
          </Grid>
        </Paper>

        {/* ===== QUESTION TYPE ===== */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography fontWeight={600} gutterBottom>
            Question Type
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={isMC}
                onChange={toggleAnswerType}
              />
            }
            label={isMC ? "Multiple Choice" : "Fill in the Blank"}
          />
        </Paper>

        {/* ===== CONTEXT ===== */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography fontWeight={600} gutterBottom>
            Context
          </Typography>

          <TextField
            multiline
            minRows={4}
            value={localQ.passage || localQ.context || ""}
            onChange={(e) => updateField("passage", e.target.value)}
            fullWidth
          />
        </Paper>

        {/* ===== QUESTION ===== */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography fontWeight={600} gutterBottom>
            Question
          </Typography>

          <TextField
            multiline
            minRows={2}
            value={localQ.question || localQ.prompt || ""}
            onChange={(e) => updateField("question", e.target.value)}
            fullWidth
          />
        </Paper>

        {/* ===== ANSWER CHOICES (MC ONLY) ===== */}
        {isMC && (
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography fontWeight={600} gutterBottom>
              Answer Choices
            </Typography>

            <Box display="flex" flexDirection="column" gap={2}>
              {["A", "B", "C", "D"].map((letter) => (
                <TextField
                  key={letter}
                  label={`Choice ${letter}`}
                  value={localQ.answerChoices?.[letter] ?? ""}
                  onChange={(e) =>
                    updateChoice(letter, e.target.value)
                  }
                  fullWidth
                />
              ))}
            </Box>
          </Paper>
        )}

        {/* ===== CORRECT ANSWER ===== */}
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography fontWeight={600} gutterBottom>
            Correct Answer
          </Typography>

          <TextField
            label={
              isMC
                ? "Correct Choice (A-D)"
                : "Correct Answer (text)"
            }
            value={
              localQ.answer?.correct ??
              localQ.correctAnswer ??
              ""
            }
            onChange={(e) =>
              updateAnswer("correct", e.target.value)
            }
            fullWidth
          />
        </Paper>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={onClose}
          sx={{
            borderRadius: "25px",
            py: 1,
            px: 2.75,
            fontWeight: 600,
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          sx={{
            borderRadius: "25px",
            py: 1,
            px: 2.75,
            fontWeight: 600,
          }}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditQuestion;
